import { useState } from "react";
import { Link } from "react-router-dom";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import PaymentModal from "../components/PaymentModal";
import Footer from "../components/Footer";

export default function Pricing() {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [successMessage, setSuccessMessage] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'annual'
  
  // Payment modal state
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    plan: '',
    planName: '',
    price: 0
  });

  const handleUpgrade = (planType) => {
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      window.location.href = '/login';
      return;
    }

    const planConfig = {
      free: { name: 'Free', price: 0 },
      pro: { name: 'Pro', price: billingPeriod === 'annual' ? 90 : 15 }, // $90/year or $15/month
      enterprise: { name: 'Enterprise', price: billingPeriod === 'annual' ? 290 : 29 } // $290/year or $29/month
    };

    const config = planConfig[planType];
    if (config) {
      setPaymentModal({
        isOpen: true,
        plan: planType,
        planName: config.name,
        price: config.price
      });
    }
  };

  const handlePaymentSuccess = async (newPlan) => {
    const planName = paymentModal.planName;
    setPaymentModal({ isOpen: false, plan: '', planName: '', price: 0 });
    
    // Refresh user data to get updated plan information
    await refreshUser();
    
    // Show appropriate success message
    if (newPlan === 'free') {
      setSuccessMessage('Successfully downgraded to Free plan!');
    } else {
      setSuccessMessage(`Successfully upgraded to ${planName} plan!`);
    }
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
        <div className="flex items-center justify-between">
          {/* Logo - Larger on mobile */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <img 
              src="/logo-icon.png" 
              alt="SiteScope" 
              className="w-12 h-12 sm:w-12 sm:h-12 md:w-14 md:h-14 transition-transform group-hover:scale-105" 
            />
            <span className="text-2xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              SiteScope
            </span>
          </Link>
          
          {/* Navigation - Clean mobile design */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Home link - text on mobile */}
            <Link 
              to="/" 
              className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors hidden sm:block"
            >
              Home
            </Link>
            
            {isAuthenticated ? (
              <>
                <Button as={Link} to="/dashboard" variant="ghost" size="sm" light className="text-sm sm:text-base px-3 sm:px-4 py-2 font-medium">
                  Dashboard
                </Button>
                <Button as={Link} to="/profile" size="sm" light className="text-sm sm:text-base px-4 sm:px-6 py-2 shadow-lg font-semibold">
                  Profile
                </Button>
              </>
            ) : (
              <>
                <Button as={Link} to="/login" variant="ghost" size="sm" light className="text-sm sm:text-base px-3 sm:px-4 py-2 font-medium">
                  Sign in
                </Button>
                <Button as={Link} to="/register" size="sm" light className="text-sm sm:text-base px-4 sm:px-6 py-2 shadow-lg font-semibold">
                  Start Free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Success Message */}
      {successMessage && (
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <p className="text-green-800 dark:text-green-200 font-medium">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-24">
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6">
            Simple, transparent pricing
          </h1>
          <p className="text-base sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto px-4">
            Start free and scale as you grow. No hidden fees, no surprises. 
            Pay only for what you need.
          </p>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mt-6 sm:mt-8 px-4">
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <span className="text-2xl">💯</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">30-Day Money Back</span>
            </div>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <span className="text-2xl">🔒</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">Secure Payment</span>
            </div>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <span className="text-2xl">⚡</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">Cancel Anytime</span>
            </div>
          </div>
          
          {/* Billing Period Toggle */}
          <div className="flex items-center justify-center gap-3 mt-8">
            <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                billingPeriod === 'annual' ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingPeriod === 'annual' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingPeriod === 'annual' ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
              Annual
            </span>
            {billingPeriod === 'annual' && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                Save 17% 🎉
              </span>
            )}
          </div>
        </div>

        <div className="grid gap-6 sm:gap-8 lg:grid-cols-3 max-w-6xl mx-auto mb-6 sm:mb-8">
          {/* Free Plan */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow flex flex-col min-h-[500px]">
            <div className="text-center mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Free</h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">Perfect for getting started</p>
              <div className="mb-4 sm:mb-6">
                <span className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100">₱0</span>
                <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">/month</span>
              </div>
            </div>
            
            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 flex-grow">
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">3 websites monitoring</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Email alerts</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">5-minute check intervals</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">30-day uptime history</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">SSL monitoring</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Community support</span>
              </div>
            </div>
            
            <div className="mt-auto pt-4">
              <Button 
                as={Link} 
                to={isAuthenticated ? "/dashboard" : "/register"}
                light
                className="w-full"
                variant={isAuthenticated && (user?.plan === 'free' || !user?.plan) ? "outline" : "primary"}
              >
                {isAuthenticated && (user?.plan === 'free' || !user?.plan) ? "Current Plan" : (isAuthenticated ? "Go to Dashboard" : "Start Free")}
              </Button>
              
              {/* Show downgrade option for paid users */}
              {isAuthenticated && user?.plan && user.plan !== 'free' && (
                <Button 
                  onClick={() => handleUpgrade('free')}
                  light
                  className="w-full mt-2 py-2 text-sm"
                  variant="ghost"
                >
                  Downgrade to Free
                </Button>
              )}
            </div>
          </div>

          {/* Pro Plan */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border-2 border-blue-500 shadow-lg hover:shadow-xl transition-shadow relative flex flex-col min-h-[500px]">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
              <span className="bg-blue-500 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-medium">
                Most Popular
              </span>
            </div>
            
            <div className="text-center mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Pro</h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">For growing businesses</p>
              <div className="mb-4 sm:mb-6">
                {billingPeriod === 'annual' ? (
                  <>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-2xl text-gray-400 dark:text-gray-500 line-through">₱9,000</span>
                      <div className="flex flex-col items-center">
                        <span className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100">₱4,500</span>
                        <span className="text-lg text-gray-500 dark:text-gray-400">$90 USD</span>
                      </div>
                    </div>
                    <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">/year (save ₱4,500)</span>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col items-center">
                      <span className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100">₱750</span>
                      <span className="text-lg text-gray-500 dark:text-gray-400">$15 USD</span>
                    </div>
                    <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">/month</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 flex-grow">
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">15 websites monitoring</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Email + Push notifications</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">2-minute check intervals</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">90-day uptime history</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Priority support</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Custom alert emails</span>
              </div>
            </div>
            
            <div className="mt-auto pt-4">
              <Button 
                onClick={() => handleUpgrade('pro')}
                light
                className="w-full"
                variant={user?.plan === 'pro' ? "outline" : "primary"}
                disabled={user?.plan === 'pro'}
              >
                {user?.plan === 'pro' ? "Current Plan" : (isAuthenticated ? "Upgrade to Pro" : "Start Pro Trial")}
              </Button>
            </div>
          </div>

          {/* Enterprise Plan */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow flex flex-col min-h-[500px]">
            <div className="text-center mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Enterprise</h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">For large organizations</p>
              <div className="mb-4 sm:mb-6">
                {billingPeriod === 'annual' ? (
                  <>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-2xl text-gray-400 dark:text-gray-500 line-through">₱17,400</span>
                      <div className="flex flex-col items-center">
                        <span className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100">₱14,500</span>
                        <span className="text-lg text-gray-500 dark:text-gray-400">$290 USD</span>
                      </div>
                    </div>
                    <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">/year (save ₱2,900)</span>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col items-center">
                      <span className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100">₱1,450</span>
                      <span className="text-lg text-gray-500 dark:text-gray-400">$29 USD</span>
                    </div>
                    <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">/month</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 flex-grow">
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">200 websites monitoring</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">All notification types</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">30-second check intervals</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">1-year uptime history</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">24/7 monitoring</span>
              </div>
            </div>
            
            <div className="mt-auto pt-4">
              <Button 
                onClick={() => handleUpgrade('enterprise')}
                light
                className="w-full"
                variant={user?.plan === 'enterprise' ? "outline" : "primary"}
                disabled={user?.plan === 'enterprise'}
              >
                {user?.plan === 'enterprise' ? "Current Plan" : "Upgrade to Enterprise"}
              </Button>
            </div>
          </div>

        </div>

        {/* Current Plan Status for Logged-in Users */}
        {isAuthenticated && (
          <div className="mt-16 text-center">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-8 border border-blue-200 dark:border-blue-800 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-2">
                Your Current Plan
              </h3>
              <p className="text-blue-700 dark:text-blue-300 mb-4">
                You're currently on the <strong>{user?.plan || 'Free'}</strong> plan
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button as={Link} to="/dashboard" variant="outline" light className="px-6 py-2">
                  Go to Dashboard
                </Button>
                <Button as={Link} to="/profile" variant="primary" light className="px-6 py-2">
                  Manage Account
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Plan Comparison Table */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-gray-100 mb-12">
            Compare plans
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full max-w-5xl mx-auto bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-white">Free</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-white">Pro</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-white">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">Websites</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 dark:text-gray-300">3</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 dark:text-gray-300">15</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 dark:text-gray-300">200</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">Check Interval</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 dark:text-gray-300">5 min</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 dark:text-gray-300">2 min</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 dark:text-gray-300">1 min</td>
                </tr>
                <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">History</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 dark:text-gray-300">30 days</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 dark:text-gray-300">90 days</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-700 dark:text-gray-300">Unlimited</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">Email Alerts</td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                </tr>
                <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">Push Notifications</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-400 dark:text-gray-500">—</td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">SSL Monitoring</td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                </tr>
                <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">Priority Support</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-400 dark:text-gray-500">—</td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">API Access</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-400 dark:text-gray-500">—</td>
                  <td className="px-6 py-4 text-sm text-center text-gray-400 dark:text-gray-500">—</td>
                  <td className="px-6 py-4 text-sm text-center"><span className="text-green-500 text-lg">✓</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Social Proof / Testimonials */}
        <div className="mt-20 bg-white dark:bg-gray-800 rounded-2xl p-8 sm:p-12 border border-gray-200 dark:border-gray-700">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-gray-100 mb-12">
            Trusted by developers and agencies
          </h2>
          
          <div className="grid gap-8 md:grid-cols-3 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">99.9%</div>
              <div className="text-gray-600 dark:text-gray-400">Uptime monitoring</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">&lt;2min</div>
              <div className="text-gray-600 dark:text-gray-400">Alert response time</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">24/7</div>
              <div className="text-gray-600 dark:text-gray-400">Continuous monitoring</div>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-lg text-gray-600 dark:text-gray-400 italic max-w-3xl mx-auto">
              "SiteScope has been instrumental in helping us maintain 99.9% uptime for our clients. 
              The instant alerts and intuitive dashboard make it easy to stay on top of any issues."
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-gray-100 mb-12">
            Frequently asked questions
          </h2>
          
          <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Can I change plans anytime?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                What happens if I exceed my plan limits?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                We'll notify you when you're approaching your limits. You can upgrade or add extra sites as needed.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Do you offer refunds?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Yes! 30-day money-back guarantee.</strong> If you're not satisfied for any reason within 30 days, contact us at support@sitescope.work for a full refund. No questions asked.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Is there a free trial?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Our free plan is always free! For paid plans, we offer a 14-day free trial.
              </p>
            </div>
          </div>
        </div>
      </main>

      <PaymentModal
        isOpen={paymentModal.isOpen}
        onClose={() => setPaymentModal({ isOpen: false, plan: '', planName: '', price: 0 })}
        plan={paymentModal.plan}
        planName={paymentModal.planName}
        price={paymentModal.price}
        currentPlan={user?.plan || 'free'}
        onSuccess={handlePaymentSuccess}
      />
      
      {/* Footer */}
      <Footer />
    </div>
  );
}


